import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import JSZip from 'jszip';
import { supabase } from '../lib/supabase';
import logo from '../logo.png';
import CreatedBy from '../components/CreatedBy';

const MAX_SIZE = 5 * 1024 * 1024 * 1024;
// 複数ファイルはブラウザのメモリ上でZIP化するため、合計サイズを制限する
const MAX_MULTI_SIZE = 1 * 1024 * 1024 * 1024;
const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB

function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export default function UploadPage() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const dragDepth = useRef(0);

  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [expiryDays, setExpiryDays] = useState(7);
  const [password, setPassword] = useState('');
  const [usePassword, setUsePassword] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [speed, setSpeed] = useState(0);
  const [statusText, setStatusText] = useState('');
  const [error, setError] = useState('');

  const totalSize = files.reduce((sum, f) => sum + f.size, 0);

  // アップロード中の誤タブ閉じ・リロードを警告
  useEffect(() => {
    if (!uploading) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [uploading]);

  const handleFiles = (newFiles) => {
    if (!newFiles || newFiles.length === 0) return;
    const arr = Array.from(newFiles);
    setFiles((prev) => {
      const merged = [...prev, ...arr];
      const total = merged.reduce((sum, f) => sum + f.size, 0);
      if (total > MAX_SIZE) { setError('合計ファイルサイズは最大5GBまでです'); return prev; }
      if (merged.length > 1 && total > MAX_MULTI_SIZE) {
        setError('複数ファイルをまとめる場合は合計1GBまでです。大きいファイルは1つずつ送信してください');
        return prev;
      }
      setError('');
      return merged;
    });
  };

  const removeFile = (index) => setFiles((prev) => prev.filter((_, i) => i !== index));

  const onDrop = useCallback((e) => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  }, []);

  // onDragLeaveは子要素に乗るたび発火するため、深さを数えてちらつきを防ぐ
  const onDragEnter = (e) => {
    e.preventDefault();
    dragDepth.current += 1;
    setDragging(true);
  };
  const onDragLeave = () => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setProgress(0);
    setError('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('セッションが切れました。再ログインしてください');
        setUploading(false);
        return;
      }
      const authHeaders = { Authorization: `Bearer ${session.access_token}` };

      let uploadFile;
      let fileName;

      if (files.length === 1) {
        uploadFile = files[0];
        fileName = files[0].name;
      } else {
        setStatusText('ZIPにまとめています...');
        const zip = new JSZip();
        for (const f of files) zip.file(f.name, f);
        const zipBlob = await zip.generateAsync(
          { type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } },
          (meta) => setProgress(Math.round(meta.percent / 2))
        );
        const base = files[0].name.replace(/\.[^.]+$/, '');
        fileName = `${base}_他${files.length - 1}件.zip`;
        uploadFile = new File([zipBlob], fileName, { type: 'application/zip' });
      }

      setStatusText('アップロード中...');
      setProgress(files.length > 1 ? 50 : 0);

      // アップロード初期化
      const initRes = await axios.post('/api/upload/init.php', {
        fileName,
        fileSize: uploadFile.size,
        expiryDays,
      }, { headers: authHeaders });
      const { fileId } = initRes.data;

      // チャンクアップロード
      const totalChunks = Math.ceil(uploadFile.size / CHUNK_SIZE);
      const progressOffset = files.length > 1 ? 50 : 0;
      const progressScale  = files.length > 1 ? 0.5 : 1;
      let lastLoaded = 0;
      let lastTime = Date.now();

      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end   = Math.min(start + CHUNK_SIZE, uploadFile.size);
        const chunk = uploadFile.slice(start, end);

        const formData = new FormData();
        formData.append('fileId', fileId);
        formData.append('chunkIndex', i);
        formData.append('chunk', chunk);

        // 一時的な通信エラーで全体が失敗しないよう、チャンク単位でリトライ
        for (let attempt = 0; ; attempt++) {
          try {
            await axios.post('/api/upload/chunk.php', formData, {
              onUploadProgress: (e) => {
                const loaded = i * CHUNK_SIZE + e.loaded;
                const pct = Math.round(progressOffset + (loaded / uploadFile.size) * 100 * progressScale);
                setProgress(pct);
                const now = Date.now();
                const elapsed = (now - lastTime) / 1000;
                if (elapsed > 0.5) {
                  setSpeed((loaded - lastLoaded) / elapsed);
                  lastTime = now;
                  lastLoaded = loaded;
                }
              },
            });
            break;
          } catch (chunkErr) {
            if (attempt >= 2 || chunkErr.response?.status < 500) throw chunkErr;
            setStatusText('再接続中...');
            await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
            setStatusText('アップロード中...');
          }
        }
      }

      // アップロード完了
      const completeRes = await axios.post('/api/upload/complete.php', {
        fileId,
        password: usePassword && password ? password : null,
      }, { headers: authHeaders });

      navigate(`/complete/${fileId}`, {
        state: {
          expiresAt: completeRes.data.expiresAt,
          password: usePassword && password ? password : null,
          fileName,
          fileCount: files.length,
        },
      });
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) setError('セッションが切れました。再ログインしてください');
      else setError(err.response?.data?.error || 'アップロードに失敗しました');
      setUploading(false);
      setStatusText('');
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4" style={{ background: '#F5F2EC' }}>
      <CreatedBy />
      <button
        onClick={() => supabase.auth.signOut()}
        className="fixed top-4 right-5 text-xs hover:underline"
        style={{ color: '#6E6A62' }}
      >
        ログアウト
      </button>
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <a href="/" className="inline-block" style={{ cursor: 'pointer' }}>
            <img src={logo} alt="and to" style={{ width: '120px', display: 'block' }} />
            <p className="text-xs uppercase text-center" style={{ color: '#6E6A62', width: '120px', marginTop: '8px', letterSpacing: '0.2em', whiteSpace: 'nowrap' }}>
              FILE TRANSFER
            </p>
          </a>
        </div>


        <div
          className="rounded-2xl p-8"
          style={{ background: '#FDFAF5', border: '1px solid #E2DDD4', boxShadow: '0 4px 24px rgba(45,42,36,0.07)' }}
        >
          {/* Drop zone */}
          <div
            role="button"
            tabIndex={uploading ? -1 : 0}
            aria-label="ファイルを選択"
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && !uploading) {
                e.preventDefault();
                inputRef.current?.click();
              }
            }}
            onClick={() => !uploading && inputRef.current?.click()}
            onDrop={onDrop}
            onDragOver={(e) => e.preventDefault()}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            className={`rounded-xl p-10 text-center cursor-pointer transition-all duration-300 ${uploading ? 'cursor-not-allowed opacity-60' : ''}`}
            style={{
              border: `2px dashed ${dragging ? '#C8694A' : '#E2DDD4'}`,
              background: dragging ? '#F5E6E0' : '#F5F2EC',
            }}
          >
            <input ref={inputRef} type="file" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} disabled={uploading} />
            {files.length === 0 ? (
              <div>
                <div className="text-4xl mb-3">↑</div>
                <p className="font-medium text-sm" style={{ color: '#2D2A24' }}>ファイルをドロップ</p>
                <p className="text-xs mt-1" style={{ color: '#6E6A62' }}>または クリックして選択（複数可）</p>
                <p className="text-xs mt-3" style={{ color: '#8C8880' }}>1ファイル最大5GB / 複数まとめては合計1GBまで</p>
              </div>
            ) : (
              <div>
                <div className="text-3xl mb-2">{files.length > 1 ? '🗂' : '📄'}</div>
                <p className="font-semibold text-sm break-all" style={{ color: '#2D2A24' }}>
                  {files.length === 1 ? files[0].name : `${files.length} ファイル選択中`}
                </p>
                <p className="text-xs mt-1" style={{ color: '#6E6A62' }}>{formatBytes(totalSize)}</p>
                {!uploading && <p className="text-xs mt-2" style={{ color: '#C8694A' }}>クリックしてファイルを追加</p>}
              </div>
            )}
          </div>

          {/* File list */}
          {files.length > 0 && !uploading && (
            <div className="mt-3 space-y-1 max-h-36 overflow-y-auto">
              {files.map((f, i) => (
                <div key={i} className="flex items-center justify-between rounded-lg px-3 py-1.5"
                  style={{ background: '#F5F2EC', border: '1px solid #E2DDD4' }}>
                  <span className="text-xs truncate flex-1" style={{ color: '#2D2A24' }}>{f.name}</span>
                  <span className="text-xs ml-2 flex-shrink-0" style={{ color: '#6E6A62' }}>{formatBytes(f.size)}</span>
                  <button onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                    aria-label={`${f.name} を削除`}
                    className="ml-2 text-xs flex-shrink-0" style={{ color: '#C8694A' }}>✕</button>
                </div>
              ))}
            </div>
          )}

          {error && <p className="mt-3 text-xs text-center" style={{ color: '#C8694A' }}>{error}</p>}

          {/* Settings */}
          {!uploading && (
            <div className="mt-6 space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#6E6A62' }}>有効期限</label>
                <select value={expiryDays} onChange={(e) => setExpiryDays(Number(e.target.value))}
                  className="text-sm rounded-lg px-3 py-1.5 focus:outline-none"
                  style={{ background: '#F5F2EC', border: '1px solid #E2DDD4', color: '#2D2A24' }}>
                  {[1, 3, 7, 14, 30].map((d) => <option key={d} value={d}>{d}日</option>)}
                </select>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#6E6A62' }}>パスワード保護</label>
                  <button onClick={() => setUsePassword(!usePassword)}
                    role="switch" aria-checked={usePassword} aria-label="パスワード保護"
                    className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200"
                    style={{ background: usePassword ? '#6B8F71' : '#E2DDD4' }}>
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${usePassword ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
                {usePassword && (
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="パスワードを入力" className="mt-2 w-full rounded-lg px-3 py-2 text-sm focus:outline-none"
                    style={{ background: '#F5F2EC', border: '1px solid #E2DDD4', color: '#2D2A24' }} />
                )}
              </div>
            </div>
          )}

          {/* Progress */}
          {uploading && (
            <div className="mt-6">
              <div className="flex justify-between text-xs mb-2" style={{ color: '#6E6A62' }}>
                <span>{statusText || 'アップロード中...'}</span>
                <span>{progress}%</span>
              </div>
              <div style={{ width: '100%', height: '3px', borderRadius: '2px', background: '#E2DDD4', overflow: 'hidden' }}>
                <div style={{ height: '100%', background: '#2D2A24', width: `${progress}%`, transition: 'width 0.3s ease' }} />
              </div>
              {speed > 0 && <p className="text-xs mt-1.5 text-right" style={{ color: '#8C8880' }}>{formatBytes(speed)}/s</p>}
            </div>
          )}

          <button onClick={handleUpload} disabled={files.length === 0 || uploading}
            className="mt-6 w-full py-3 rounded-xl font-semibold text-sm tracking-wide transition-all duration-200 active:scale-[0.98]"
            style={files.length > 0 && !uploading
              ? { background: '#2D2A24', color: '#F5F2EC' }
              : { background: '#E2DDD4', color: '#6E6A62', cursor: 'not-allowed' }}>
            {uploading ? (statusText || 'アップロード中...') : 'アップロード'}
          </button>
        </div>
      </div>
    </div>
  );
}
